'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import enStrings from '../app/locales/en.json';

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  // Default to English, or use localStorage if set
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });
  // Initialise with English strings so the very first render never shows raw keys
  const [strings, setStrings] = useState(enStrings);

  useEffect(() => {
    const i18nSource = process.env.NEXT_PUBLIC_I18N_SOURCE || 'local';
    const paymentsHost = process.env.NEXT_PUBLIC_PAYMENTS_HOST;

    async function loadStrings() {
      const lang = language || 'en';

      // Remote source: fetch from translations API (Beta/main API)
      const apiBase = process.env.NEXT_PUBLIC_API_BASE_URL || paymentsHost;
      if (i18nSource === 'remote' && apiBase) {
        try {
          // Add a 5-second timeout to prevent indefinite hanging
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          const res = await fetch(`${apiBase}/api/translations/${lang}`, {
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (res.ok) {
            const data = await res.json();
            setStrings(data.data || data || {});
            return;
          }
        } catch (err) {
          // Timeout or network error — fall through to local import
          console.warn(`[i18n] Failed to fetch remote translations for '${lang}':`, err.message);
        }
      }

      // Local source (default): import from locales folder
      const mod = await import(`../app/locales/${lang}.json`);
      setStrings(mod.default || {});
    }
    loadStrings();
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, strings }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
